export interface ReviewQuestion {
    id: number;
    review_question_text: string;
    review_entity_type: string;
    description: string;
    review_dimension: string;
    review_frequency: string;
    answer_type: string;
    answer_options: string[];
    review_question_type: string;
    action_required: string;
    default_value: string;
    ideal_value: string;
    created_by: string;
    created_at: string;
    updated_by: string;
    updated_at: string;
}

export interface Role {
    role_id: number;
    role_name: string;
}

export interface User {
    id: number;
    user_name: string;
    email?: string;
    display_name?: string;
    external_id?: string;
    upn?: string;
    attributes?: { [key: string]: any };
    preferences?: { [key: string]: string };
    status?: string;
    created_at?: string;
    updated_at?: string;
    last_login_at?: string;
    roles: Role[];
    client_id?: string;
}

// Utility functions for User operations (matching backend UserRole methods)
export const UserUtils = {
    hasRole: (user: User | null | undefined, roleName: string): boolean => {
        if (!user?.roles) return false;
        return user.roles.some(role => role.role_name.toUpperCase() === roleName.toUpperCase());
    },

    isAdmin: (user: User | null | undefined): boolean => {
        return UserUtils.hasRole(user, 'ADMIN');
    },

    isReviewer: (user: User | null | undefined): boolean => {
        return UserUtils.hasRole(user, 'REVIEWER');
    },

    isReviewee: (user: User | null | undefined): boolean => {
        return UserUtils.hasRole(user, 'REVIEWEE');
    },

    isDataEntrySupport: (user: User | null | undefined): boolean => {
        return UserUtils.hasRole(user, 'DATA_ENTRY_SUPPORT');
    },

    /** Home / navigation access to Review Management (reviewer, reviewee, or data-entry support). */
    canAccessReviewManagement: (user: User | null | undefined): boolean => {
        return (
            UserUtils.isReviewer(user) ||
            UserUtils.isReviewee(user) ||
            UserUtils.isDataEntrySupport(user)
        );
    },

    /**
     * Create a new questionnaire response / start a review.
     * DATA_ENTRY_SUPPORT may enter data on existing reviews but must not create reviews.
     */
    canCreateReview: (user: User | null | undefined): boolean => {
        return UserUtils.isReviewee(user) || UserUtils.isAdmin(user);
    },

    /**
     * Final submit of a questionnaire response.
     * DATA_ENTRY_SUPPORT may answer/edit but must not submit.
     */
    canSubmitReview: (user: User | null | undefined): boolean => {
        return UserUtils.isReviewee(user) || UserUtils.isAdmin(user);
    },

    /**
     * Perform/close a review and add reviewer comments (PERFORM_REVIEW).
     * DATA_ENTRY_SUPPORT must not perform reviews.
     */
    canPerformReview: (user: User | null | undefined): boolean => {
        return UserUtils.isReviewer(user) || UserUtils.isAdmin(user);
    },
    
    getAttribute: (user: User, key: string, defaultValue: any = null): any => {
        if (user.attributes && typeof user.attributes === 'object') {
            return user.attributes[key] ?? defaultValue;
        }
        return defaultValue;
    },
    
    hasAttribute: (user: User, key: string): boolean => {
        if (user.attributes && typeof user.attributes === 'object') {
            return key in user.attributes;
        }
        return false;
    },
    
    getLocation: (user: User): string | null => {
        return UserUtils.getAttribute(user, 'location');
    },
    
    getOrgRole: (user: User): string | null => {
        return UserUtils.getAttribute(user, 'org_role');
    }
};

export interface LoginResponse {
    access_token: string;
}

export interface ProjectType {
    id: string;
    name: string;
    description: string;
}